import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySelectionListChange as MatSelectionListChange } from '@angular/material/legacy-list';
import { forkJoin } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ContractPerimeterModel } from 'src/app/core/models/contract.model';
import { ProductModel } from 'src/app/core/models/product.model';
import { ProductService } from '../../../product/product.service';

@Component({
  selector: 'vex-product-edit-multiple',
  templateUrl: './product-edit-multiple.component.html',
  styleUrls: ['./product-edit-multiple.component.scss'],
})
export class ProductEditMultipleComponent implements OnInit {
  products: ProductModel[] = [];
  /** Filtered products after search/engagement filter */
  displayedProducts: ProductModel[] = [];
  /** Selected product ids */
  selection: Set<string> = new Set<string>();
  /** Engagement filter */
  engagements: { id: string; label: string }[] = [];
  engagementCtrl = new UntypedFormControl('');
  searchCtrl = new UntypedFormControl('');

  form: UntypedFormGroup;
  perimeter: ContractPerimeterModel[] = [];

  /** Step by step editing */
  isStepMode = false;
  private editQueue: ProductModel[] = [];
  currentProduct: ProductModel | null = null;
  private currentIndex = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { products: ProductModel[]; forceStepMode?: boolean },
    private dialogRef: MatDialogRef<ProductEditMultipleComponent>,
    private fb: UntypedFormBuilder,
    private productService: ProductService,
  ) {}

  ngOnInit(): void {
    this.products = (this.data.products ?? []).sort((a, b) => {
      const aLabel = a.contract?.label ?? '';
      const bLabel = b.contract?.label ?? '';
      return aLabel.localeCompare(bLabel);
    });
    this.displayedProducts = [...this.products];
    this.form = this.fb.group({
      endValidityDate: [null],
      label: [''],
      comment: [''],
      isAllPerimeter: [true],
    });

    // build engagement options if several contracts are present
    const map = new Map<string, string>();
    this.products.forEach(p => {
      if (p.contract?._id) {
        map.set(p.contract._id, p.contract.label);
      } else if (p.contractId) {
        map.set(p.contractId, p.contractId);
      }
    });
    this.engagements = Array.from(map.entries()).map(([id, label]) => ({ id, label }));

    this.engagementCtrl.valueChanges.subscribe(() => this.applyFilters());
    this.searchCtrl.valueChanges.pipe(debounceTime(300)).subscribe(() => this.applyFilters());
    // If forced single-step mode, initialize edit queue directly
    if (this.data.forceStepMode) {
      this.editQueue = this.products.slice(0, 1);
      this.isStepMode = true;
      this.loadCurrent();
    }
  }

  private applyFilters(): void {
    const engagement = this.engagementCtrl.value as string;
    const term = (this.searchCtrl.value ?? '').toString().toLowerCase();
    this.displayedProducts = this.products
      .filter(p => {
        const matchEngagement = !engagement || p.contractId === engagement || p.contract?._id === engagement;
        const matchSearch = !term || p.label?.toLowerCase().includes(term) || p.reference?.toLowerCase().includes(term);
        return matchEngagement && matchSearch;
      })
      .sort((a, b) => {
        const aLabel = a.contract?.label ?? '';
        const bLabel = b.contract?.label ?? '';
        return aLabel.localeCompare(bLabel);
      });
  }

  toggleSelection(id: string, checked: boolean): void {
    if (checked) {
      this.selection.add(id);
    } else {
      this.selection.delete(id);
    }
  }

  onListChange(event: MatSelectionListChange): void {
    // Angular Material 14: option -> options (array)
    const option = event.options[0];
    if (option) {
      const id = option.value;
      this.toggleSelection(id, option.selected);
    }
  }

  isSelected(id: string): boolean {
    return this.selection.has(id);
  }

  onSelectedItemsChange(perimeter: ContractPerimeterModel[]) {
    this.perimeter = perimeter;
  }

  startStepEdit(): void {
    this.editQueue = Array.from(this.selection)
      .map(id => this.products.find(p => p._id === id))
      .filter((p): p is ProductModel => !!p);

    if (!this.editQueue.length) {
      return;
    }

    this.currentIndex = 0;
    this.isStepMode = true;
    this.loadCurrent();
  }

  private loadCurrent(): void {
    this.currentProduct = this.editQueue[this.currentIndex] ?? null;
    if (!this.currentProduct) {
      this.isStepMode = false;
      this.applyFilters();
      return;
    }

    const endDate = this.currentProduct.endValidityDate ? new Date(this.currentProduct.endValidityDate) : null;

    this.form.patchValue({
      endValidityDate: endDate,
      label: this.currentProduct.label ?? '',
      comment: this.currentProduct.comment ?? '',
      isAllPerimeter: this.currentProduct.isFullPerimeter ?? true,
    });
    this.perimeter = this.currentProduct.perimeter ?? [];
  }

  saveStep(): void {
    if (!this.currentProduct) {
      return;
    }

    const payload: ProductModel = {
      ...this.currentProduct,
      endValidityDate: this.form.value.endValidityDate,
      label: this.form.value.label,
      comment: this.form.value.comment,
      isFullPerimeter: this.form.value.isAllPerimeter,
      perimeter: this.form.value.isAllPerimeter ? [] : this.perimeter,
    };

    this.productService.update(payload).subscribe({
      next: x => {
        const index = this.products.findIndex(p => p._id === x.data._id);
        if (index > -1) {
          this.products[index] = x.data;
        }
        this.currentIndex++;
        if (this.currentIndex < this.editQueue.length) {
          this.loadCurrent();
        } else {
          this.isStepMode = false;
          this.applyFilters();
          this.dialogRef.close();
        }
      },
      error: () => {
        this.isStepMode = false;
      },
    });
  }

  closeStep(): void {
    this.isStepMode = false;
    this.applyFilters();
  }

  close(): void {
    this.dialogRef.close();
  }
}
